import Badge from "@intility/bifrost-react/Badge";
import Section from "@intility/bifrost-react/Section";
import Table from "@intility/bifrost-react/Table";
import type { TypeDefinition } from "~/api/fga-types";
import ModelGraph from "./ModelGraph";

function RelationDetail({ typeDef }: { typeDef: TypeDefinition }) {
  const relations = typeDef.relations ?? {};
  const metadata = typeDef.metadata?.relations ?? {};

  return (
    <div style={{ padding: 16 }}>
      <Table noBorder>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Relation</Table.HeaderCell>
            <Table.HeaderCell>Directly Assigned Types</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {Object.entries(relations).map(([name]) => (
            <Table.Row key={name}>
              <Table.Cell>
                <Badge state="chill">{name}</Badge>
              </Table.Cell>
              <Table.Cell>
                {metadata[name]?.directly_related_user_types?.map((t) => (
                  <Badge key={t.type} state="neutral" pill>
                    {t.type}
                  </Badge>
                )) ?? <span className="bfc-base-2">computed</span>}
              </Table.Cell>
            </Table.Row>
          ))}
          {Object.keys(relations).length === 0 && (
            <Table.Row>
              <Table.Cell>No relations</Table.Cell>
              <Table.Cell />
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </div>
  );
}

// The visual + tabular breakdown of an authorization model's type definitions.
// Shared by the Model page (for the active model) and the version-detail page.
export default function ModelDetails({
  typeDefinitions,
}: {
  typeDefinitions: TypeDefinition[];
}) {
  return (
    <>
      {typeDefinitions.length > 0 && (
        <Section>
          <Section.Header>Model Visualization</Section.Header>
          <Section.Content>
            <ModelGraph typeDefinitions={typeDefinitions} />
          </Section.Content>
        </Section>
      )}

      <Section>
        <Section.Header>
          Type Definitions{" "}
          <Badge state="neutral">{typeDefinitions.length}</Badge>
        </Section.Header>
        <Table noBorder>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell />
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Relations</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {typeDefinitions.map((typeDef) => (
              <Table.Row
                key={typeDef.type}
                content={<RelationDetail typeDef={typeDef} />}
                limitExpandClick
              >
                <Table.Cell>
                  <Badge state="brand">{typeDef.type}</Badge>
                </Table.Cell>
                <Table.Cell>
                  {Object.keys(typeDef.relations ?? {}).join(", ") || "none"}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Section>
    </>
  );
}
